import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { PlatformPanelOrgChatControls } from "./PlatformPanelOrgChatControls";

type OrgsTabProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  p: any;
  canManageOrgs: boolean;
};

export function PlatformPanelOrgsTab({ p, canManageOrgs }: OrgsTabProps) {
  const [chatOrgId, setChatOrgId] = useState<string | null>(null);

  return (
    <SectionCard
      title="Organizations"
      description="All platform-visible orgs. Manage to deactivate or transfer members. Chat enables Stream team chat for that org."
    >
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900/50">
        <div className="divide-y divide-gray-100 dark:divide-zinc-800">
          {p.orgs.map((o: { id: string; name: string; org_type: string | null; is_active: boolean | null }) => (
            <div key={o.id} className="divide-y divide-gray-100 dark:divide-zinc-800">
              <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-gray-900 dark:text-zinc-100">{o.name}</div>
                  <div className="text-xs text-gray-500 font-mono dark:text-zinc-400">{o.id}</div>
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  <span
                    className={
                      "text-xs px-2 py-0.5 rounded-full font-medium " +
                      (o.org_type === "internal"
                        ? "bg-indigo-100 text-indigo-800"
                        : o.org_type === "dealer"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-700")
                    }
                  >
                    {o.org_type ?? "unknown"}
                  </span>
                  {o.is_active === false && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800">
                      inactive
                    </span>
                  )}
                  {canManageOrgs ? (
                    <button
                      type="button"
                      onClick={() => setChatOrgId((id) => (id === o.id ? null : o.id))}
                      className={
                        "text-xs px-2 py-0.5 rounded-full border font-medium " +
                        (chatOrgId === o.id
                          ? "border-teal-400 bg-teal-50 text-teal-900"
                          : "border-teal-200 text-teal-800 hover:bg-teal-50 dark:border-teal-800 dark:text-teal-200")
                      }
                    >
                      {chatOrgId === o.id ? "Close chat" : "Chat"}
                    </button>
                  ) : null}
                  {canManageOrgs && o.is_active !== false && (
                    <button
                      type="button"
                      onClick={() =>
                        p.manageOrgId === o.id ? p.setManageOrgId(null) : void p.openManageOrg(o.id)
                      }
                      className={
                        "text-xs px-2 py-0.5 rounded-full border font-medium " +
                        (p.manageOrgId === o.id
                          ? "border-gray-300 text-gray-600 hover:bg-gray-50"
                          : "border-red-200 text-red-700 hover:bg-red-50")
                      }
                    >
                      {p.manageOrgId === o.id ? "Cancel" : "Manage"}
                    </button>
                  )}
                </div>
              </div>

              {chatOrgId === o.id ? (
                <PlatformPanelOrgChatControls orgId={o.id} orgName={o.name} />
              ) : null}

              {p.manageOrgId === o.id && (
                <div className="px-4 py-4 bg-red-50 border-t border-red-100 dark:bg-red-950/30 dark:border-red-900/60">
                  <div className="text-sm font-semibold text-red-900 mb-2 dark:text-red-100">Deactivate "{o.name}"</div>
                  <div className="text-sm text-red-800 mb-3 dark:text-red-200">
                    Active members:{" "}
                    <span className="font-medium">
                      {p.manageCountLoading ? "loading..." : (p.manageOrgMemberCount ?? "?")}
                    </span>
                  </div>

                  {!p.manageCountLoading && (p.manageOrgMemberCount ?? 0) > 0 && (
                    <div className="mb-3">
                      <label className="text-xs font-medium text-gray-700 block mb-1 dark:text-zinc-300">
                        Transfer members to another org:
                      </label>
                      <select
                        value={p.transferToOrgId}
                        onChange={(e) => p.setTransferToOrgId(e.target.value)}
                        className="w-full sm:w-auto text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
                      >
                        <option value="">-- no transfer (members lose org access) --</option>
                        {p.orgs
                          .filter((x: { id: string; is_active: boolean | null }) => x.id !== o.id && x.is_active !== false)
                          .map((x: { id: string; name: string }) => (
                            <option key={x.id} value={x.id}>
                              {x.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => void p.doDeactivateOrg(o)}
                      disabled={p.loading || p.manageCountLoading}
                      className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-60"
                    >
                      {(p.manageOrgMemberCount ?? 0) > 0 && p.transferToOrgId
                        ? "Transfer + Deactivate"
                        : "Deactivate Org"}
                    </button>
                    <button
                      type="button"
                      onClick={() => p.setManageOrgId(null)}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {p.orgs.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-600 dark:text-zinc-300">No orgs visible (check platform RPC grants/RLS).</div>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}
